import React, {
	useCallback,
	useMemo,
	useState,
} from 'react';
import {
	Col,
	Row,
	Tabs,
} from 'antd';
import { Moment } from 'moment/moment';
import isEqual from 'lodash.isequal';
import {
	useMutation,
	useQuery,
} from 'react-query';
import { ExportOutlined } from '@ant-design/icons';
import {
	AccountingItemApprovalStates,
	FeatureFlags,
	SenderTypes,
} from '@shared/utils/constants';
import { sortByDates } from '@shared/utils/sortByDates';
import type { GetAccountingItemsResponse } from '@api/features/financials/getAccountingItems';
import Card from '@client/components/Card/Card';
import SimpleScreen from '@client/components/screens/SimpleScreen';
import {
	archiveAccountingItem,
	exportAccountingItems,
	getAccountingItems,
	getVessels,
	postAccountingItems,
} from '@client/lib/api';
import Button from '@client/components/Button';
import { Links } from '@client/utils/links';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import { showPostAccountingItemsOutcome } from '@client/screens/financials/ArApScreen/helpers/showPostAccountingItemsOutcome';
import { getArApColumns } from '@client/screens/financials/ArApScreen/helpers/getArApColumns';
import DateFilterDropDown from '@client/components/DateFilterDropDown/DateFilterDropDown';
import { stringSetToSortedList } from '@client/utils/stringSetToSortedList';
import showErrorNotification from '@client/utils/showErrorNotification';
import Table from '@client/components/Table/Table';
import useFeatureFlag from '@client/utils/hooks/useFeatureFlag';
import styles from './AccountingItemsScreen.module.css';

type Entry = GetAccountingItemsResponse[number];

const searchAttributes = ['vesselName', 'counterparty', 'invoiceNumber', 'invoiceDescription', 'description'];

const AccountingItemsScreen = () => {
	const [selectedTab, setSelectedTab] = useState('unposted');
	const isNavigare = useFeatureFlag(FeatureFlags.NAVIGARE_ACCOUNTING);

	const options = useMemo(() => {
		if (selectedTab === 'archived') {
			return { archived: true };
		}

		if (selectedTab === 'unposted') {
			return { posted: false };
		}

		return { posted: true };
	}, [selectedTab]);

	const {
		data,
		isLoading,
		refetch,
	} = useQuery(
		['accountingItems', options],
		() => getAccountingItems(options),
		{
			refetchOnWindowFocus: false,
		},
	);

	const {
		data: vessels,
		isLoading: vesselsLoading,
	} = useQuery(
		'vessels',
		() => getVessels(),
	);

	const [selectedItems, setSelectedItems] = useState<Array<string>>([]);
	const [dateRange, setDateRange] = useState<[Moment, Moment] | null>(null);
	const [dateDropdownVisible, setDateDropdownVisible] = useState(false);

	const accountingItems = useMemo(() => {
		if (data == null) {
			return [];
		}

		const vesselNameById = new Map((vessels ?? []).map((v) => [v.id, v.name]));

		return sortByDates(data, 'date', { newestFirst: true }).map((item) => ({
			...item,
			vesselName: item.vesselId == null ? undefined : vesselNameById.get(item.vesselId),
		}));
	}, [data, vessels]);

	const filterMaps = useMemo(() => {
		const vesselsMap = new Set<string>();
		const contracts = new Set<string>();
		const itemTypes = new Set<string>();
		const approvalStates = new Set<string>();
		const counterparties = new Set<string>();
		const invoiceNumbers = new Set<string>();

		accountingItems?.forEach((item) => {
			if (item.vesselId != null) {
				const relevantVessel = vessels?.find((ves) => ves.id === item.vesselId);

				if (relevantVessel != null) {
					vesselsMap.add(relevantVessel.name);
				}
			}

			contracts.add(item.contract);
			itemTypes.add(item.type);
			approvalStates.add(item.state);
			if (item.counterparty != null) {
				counterparties.add(item?.counterparty);
			}

			if (item.invoiceNumber != null) {
				invoiceNumbers.add(item.invoiceNumber);
			}
		});

		return {
			vessels: stringSetToSortedList<string>(vesselsMap),
			contracts: stringSetToSortedList<string>(contracts),
			itemTypes: stringSetToSortedList<string>(itemTypes),
			approvalStates: stringSetToSortedList<string>(approvalStates),
			counterparties: stringSetToSortedList<string>(counterparties),
			invoiceNumbers: stringSetToSortedList<string>(invoiceNumbers),
		};
	}, [accountingItems, vessels]);

	const [filteredItems, setFilteredItems] = useState<Entry[]>(accountingItems ?? []);

	const selectAll = useCallback((deselect: boolean) => {
		if (accountingItems == null) {
			return;
		}

		if (deselect) {
			setSelectedItems([]);

			return;
		}

		if (filteredItems != null) {
			setSelectedItems(filteredItems
				.filter((ap) => !ap.posted && ap.state === AccountingItemApprovalStates.APPROVED)
				.map((ap) => ap.customId));

			return;
		}

		setSelectedItems(accountingItems
			.filter((ap) => !ap.posted && ap.state === AccountingItemApprovalStates.APPROVED)
			.map((ap) => ap.customId));
	}, [accountingItems, filteredItems]);

	const getCounterpartyUrl = (row: Entry) => {
		if (row.counterpartyId != null) {
			if (row.counterpartyType === SenderTypes.SUPPLIER) {
				return Links.Supplier.get(row.counterpartyId);
			}

			return Links.Charterer.get(row.counterpartyId);
		}

		return '/';
	};

	const onSuccess = () => {
		refetch();
		setSelectedItems([]);
	};

	const sortedItems = useMemo(() => {
		if (accountingItems == null) {
			return [];
		}

		return accountingItems.sort((a, b) => b.id - a.id);
	}, [accountingItems]);

	const exportMutation = useMutation(
		() => exportAccountingItems(),
		{
			onSuccess: (blob) => {
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.download = 'postings.xlsx';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			},
			onError: (e) => {
				showErrorNotification('Could not export postings', e as Error);
			},
		},
	);

	const onArchive = async (customId: string, archived: boolean) => {
		const action = archived ? 'unarchive' : 'archive';

		try {
			await archiveAccountingItem(customId);
			onSuccess();
			showSuccessNotification(`Successfully ${action}d item`);
		} catch (e) {
			showErrorNotification(`Could not ${action} item`, e as Error);
		}
	};

	return (
		<SimpleScreen
			title="Postings Table"
			rootPageTitle="Postings"
			headerActions={[
				(
					<Button
						onClick={() => exportMutation.mutate()}
						type="primary"
						icon={<ExportOutlined />}
						loading={exportMutation.isLoading}
					>
						Export to Excel
					</Button>
				),
				(
					<Button
						onClick={async () => {
							try {
								const submittedIds = [...selectedItems];
								const result = await postAccountingItems({ items: submittedIds });
								showPostAccountingItemsOutcome(result, 'post');
								await refetch();
								setSelectedItems((prev) => {
									const failed = new Set(result.failures.map((f) => f.customId));
									const submitted = new Set(submittedIds);

									return [
										...prev.filter((id) => !submitted.has(id)),
										...submittedIds.filter((id) => failed.has(id)),
									];
								});
							} catch (e) {
								showErrorNotification('Could not post item(s)', e as Error);
							}
						}}
						type="primary"
						disabled={selectedItems.length === 0}
					>
						Post
						{selectedItems.length > 0 && (` (${selectedItems.length})`)}
					</Button>
				),
			]}
		>
			<Row gutter={[16, 16]}>
				<Col span={24}>
					<Card slim>
						<Tabs
							onChange={setSelectedTab}
							activeKey={selectedTab}
							defaultActiveKey="unposted"
							type="card"
							items={[
								{
									key: 'unposted',
									label: 'Unposted',
									className: styles.tabs,
									children: (
										<Table<Entry>
											showSearch
											searchAttributes={searchAttributes}
											size="small"
											pagination={{ position: ['bottomCenter'] }}
											dataSource={sortedItems.filter((item) => !item.posted && !item.archived)}
											columns={getArApColumns({
												vessels,
												isNavigare,
												selectedItems,
												setSelectedItems,
												getCounterpartyUrl,
												onSuccess,
												filterMaps,
												selectAll,
												onArchive,
												dateRange,
												dateDropdownVisible,
												setDateDropdownVisible,
												filterDropdown: (
													<DateFilterDropDown
														filterValue={dateRange}
														setFilterValue={setDateRange}
														setFilterVisible={setDateDropdownVisible}
													/>
												),
											})}
											// @ts-ignore
											summary={(d) => {
												if (!isEqual(filteredItems, d)) {
													setFilteredItems(d as Entry[]);
												}
											}}
											loading={isLoading || vesselsLoading}
										/>
									),
								},
								{
									key: 'posted',
									label: 'Posted',
									className: styles.tabs,
									children: (
										<Table<Entry>
											showSearch
											searchAttributes={searchAttributes}
											pagination={{ position: ['bottomCenter'] }}
											dataSource={sortedItems.filter((item) => item.posted && !item.archived)}
											columns={getArApColumns({
												vessels,
												isNavigare,
												selectedItems,
												setSelectedItems,
												getCounterpartyUrl,
												onSuccess,
												onArchive,
												filterMaps,
												selectAll,
												dateRange,
												dateDropdownVisible,
												setDateDropdownVisible,
												filterDropdown: (
													<DateFilterDropDown
														filterValue={dateRange}
														setFilterValue={setDateRange}
														setFilterVisible={setDateDropdownVisible}
													/>
												),
											})}
											loading={isLoading || vesselsLoading}
										/>
									),
								},
								{
									key: 'archived',
									label: 'Archived',
									className: styles.tabs,
									children: (
										<Table<Entry>
											showSearch
											searchAttributes={searchAttributes}
											pagination={{ position: ['bottomCenter'] }}
											dataSource={sortedItems.filter((item) => item.archived)}
											columns={getArApColumns({
												vessels,
												isNavigare,
												selectedItems,
												setSelectedItems,
												getCounterpartyUrl,
												onSuccess,
												filterMaps,
												onArchive,
												selectAll,
												dateRange,
												dateDropdownVisible,
												setDateDropdownVisible,
												filterDropdown: (
													<DateFilterDropDown
														filterValue={dateRange}
														setFilterValue={setDateRange}
														setFilterVisible={setDateDropdownVisible}
													/>
												),
											})}
											loading={isLoading || vesselsLoading}
										/>
									),
								},
							]}
						/>
					</Card>
				</Col>
			</Row>
		</SimpleScreen>
	);
};

export default AccountingItemsScreen;
